import React, { useEffect, useMemo, useState } from 'react';
import { consumeNavPath } from '@/lib/adminNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';

import { Trash2, Plus, Edit, Eye, X, Users, KeyRound, UserPlus, Search, AlertOctagon } from 'lucide-react';

import FolderTabLayout from './ui/folder-tab-layout';
import { useAppContext, Driver } from '@/contexts/AppContext';
import { canEditTab } from '@/lib/permissions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import DriverViewModal from './DriverViewModal';
import DriverEditModal from './DriverEditModal';
import KeyFobsTab from './KeyFobsTab';
import { supabase } from '@/lib/supabase';
import { getAppTimeZone } from '@/lib/dates';
import { normalizePhoneInput } from '@/lib/phone';

type PaymentType = 'Waged' | 'Commission' | 'Contractor';
type DriverGender = 'Male' | 'Female' | 'Other' | 'PreferNotToSay';
type Urgency = 'green' | 'orange' | 'red' | 'unknown';

const NO_SELECTION = '__none__';

// Same single-tenant default used elsewhere (ClosedCheck.tsx,
// DeductionCategoriesManager.tsx) -- without it, a newly-added driver's
// tenant_id is null and they silently drop out of every tenant-scoped
// query, including the login screen's driver dropdown (see LoginForm.tsx).
const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

interface FullDriver {
  driver_name: string;
  ridewise_id: string | null;
  phone: string | null;
  email: string | null;
  AdminRights: string | null;
  created_at: string | null;
  updated_at: string | null;
  Password: string | null;
  driver_lastname: string | null;
  driver_firstname: string | null;
  gender?: DriverGender | null;
  dob?: string | null;
  include_in_reports: boolean | null;
  bank_account_number: string | null;
  bank_account_name: string | null;
  payout_reference_override: string | null;
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
  insurance_claim_free: boolean | null;
  last_vehicle_inspection_at: string | null;
  last_vehicle_inspection_shift_date: string | null;
  vehicle_inspection_shift_counter: number | null;
  payment_type: PaymentType | null;
  is_active: boolean;
  toro?: boolean | null;
  lcp_units?: boolean | null;
  dl_number?: string | null;
  dl_version?: number | null;
  dl_expiry?: string | null;
  id_expiry?: string | null;
  dl_days_left?: number | null;
  id_days_left?: number | null;
  dl_taxicaller_updated?: boolean | null;
  id_taxicaller_updated?: boolean | null;
  dl_scan_on_file?: boolean | null;
  id_scan_on_file?: boolean | null;
  dl_scan_path?: string | null;
  id_scan_path?: string | null;
  can_drive_manual?: boolean | null;
  exempt_from_inspection?: boolean | null;
}

const transmissionBadge = (val: boolean | null | undefined) => {
  if (val === true) return { label: 'Auto & Manual', className: 'bg-green-900/40 text-green-400 border-green-700/50' };
  if (val === false) return { label: 'Auto Only', className: 'bg-orange-900/40 text-orange-400 border-orange-700/50' };
  return { label: 'Not Set', className: 'bg-gray-800/40 text-gray-500 border-gray-700/50' };
};


const PAYMENT_TYPE_OPTIONS: { value: PaymentType | typeof NO_SELECTION; label: string }[] = [
  { value: NO_SELECTION, label: '— Select —' },
  { value: 'Waged', label: 'Waged' },
  { value: 'Commission', label: 'Commission' },
  { value: 'Contractor', label: 'Contractor' },
];

const GENDER_OPTIONS: { value: DriverGender | typeof NO_SELECTION; label: string }[] = [
  { value: NO_SELECTION, label: '— Select —' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
  { value: 'PreferNotToSay', label: 'Prefer not to say' },
];

const toSelectValue = (val: string | null | undefined): string => {
  if (!val || val === '') return NO_SELECTION;
  return val;
};

const fromSelectValue = (val: string): string | null => {
  if (val === NO_SELECTION) return null;
  return val;
};

const DriverManagement: React.FC = () => {
  const { currentUser, loadDriversFromDB, effectivePermissions } = useAppContext();
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked driver(s) here, read-only unless
  // explicitly given edit rights. Unrestricted (the default -- everyone
  // today) is unaffected.
  const canEdit = canEditTab(effectivePermissions, "drivers");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  const [allDrivers, setAllDrivers] = useState<FullDriver[]>([]);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  // Active sub-tab (Settings-style pill navigation)
  const [driverTab, setDriverTab] = useState<'drivers' | 'add' | 'keyfobs' | 'taxicaller'>('drivers');

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[0]) setDriverTab(path[0] as 'drivers' | 'add' | 'keyfobs' | 'taxicaller');
  }, []);

  // Add New Driver / TaxiCaller Sync are fleet-wide admin/onboarding tools
  // with no per-driver scope -- hidden from a restricted user's tab list
  // below; redirect away if a deep-link or stale state lands on one directly.
  useEffect(() => {
    if (scopedDriverNames && (driverTab === 'add' || driverTab === 'taxicaller')) {
      setDriverTab('drivers');
    }
  }, [scopedDriverNames, driverTab]);

  // TaxiCaller sync review
  const [driverMismatches, setDriverMismatches] = useState<any[]>([]);
  const [pushingDriverName, setPushingDriverName] = useState<string | null>(null);
  const [unlinkedDrivers, setUnlinkedDrivers] = useState<{ driver_name: string; taxicaller_link_status: string }[]>([]);
  const [linkingDriverName, setLinkingDriverName] = useState<string | null>(null);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkCandidates, setLinkCandidates] = useState<{ id: number; first_name: string; last_name: string }[]>([]);
  const [linkSearching, setLinkSearching] = useState(false);

  // Drivers list filters
  const [driverSearch, setDriverSearch] = useState('');
  const [complianceFilter, setComplianceFilter] = useState<'all' | Urgency>('all');


  // Modal states
  const [editingDriver, setEditingDriver] = useState<FullDriver | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingDriver, setViewingDriver] = useState<Driver | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [newDriver, setNewDriver] = useState<Partial<FullDriver>>({
    driver_name: '',
    driver_firstname: '',
    driver_lastname: '',
    ridewise_id: '',
    email: '',
    phone: '',
    Password: '',
    AdminRights: 'No',
    include_in_reports: true,
    payment_type: '',
    is_active: true,
    gender: '',
    dob: '',
  });

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return String(dateStr).split('T')[0];
  };

  const calcAge = (dobStr: string | null | undefined): number | null => {
    if (!dobStr) return null;
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    return age;
  };

  const formatAgeLabel = (age: number | null) => {
    if (age === null) return '-';
    if (age < 0 || age > 120) return 'Check DOB';
    return String(age);
  };

  const urgencyForDays = (days: number | null | undefined): Urgency => {
    if (days === null || days === undefined || typeof days !== 'number') return 'unknown';
    if (days < 30) return 'red';
    if (days < 90) return 'orange';
    return 'green';
  };

  const driverUrgency = (d: FullDriver): Urgency => {
    const u1 = urgencyForDays(d.dl_days_left);
    const u2 = urgencyForDays(d.id_days_left);
    const rank: Record<Urgency, number> = { red: 3, orange: 2, green: 1, unknown: 0 };
    return rank[u1] >= rank[u2] ? u1 : u2;
  };

  const daysTextClass = (days: number | null | undefined) => {
    const u = urgencyForDays(days);
    if (u === 'red') return 'text-red-400 font-semibold';
    if (u === 'orange') return 'text-orange-400 font-semibold';
    if (u === 'green') return 'text-green-500 font-semibold';
    return 'text-gray-500 italic';
  };

  const formatDaysLeft = (days: number | null | undefined) => {
    if (days === null || days === undefined || typeof days !== 'number') return '-';
    if (days < 0) return `Expired (${days})`;
    return `${days}`;
  };

  const loadAllDrivers = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('drivers_compliance_v').select('*').order('driver_firstname', { ascending: true });

    if (error) {
      console.error('Error loading all drivers:', error);
      toast({ title: 'Error', description: 'Failed to load drivers', variant: 'destructive' });
      setLoading(false);
      return;
    }

    setAllDrivers((data as FullDriver[]) || []);
    setLoading(false);
  };

  const loadTaxicallerData = async () => {
    const { data: mData, error: mErr } = await supabase
      .from('driver_sync_mismatches')
      .select('id,driver_name,field_name,dms_value,taxicaller_value,last_seen_at')
      .eq('status', 'pending')
      .order('driver_name');
    if (mErr) {
      toast({ title: 'Error loading TaxiCaller sync', description: mErr.message, variant: 'destructive' });
    } else {
      setDriverMismatches(mData || []);
    }

    const { data: uData, error: uErr } = await supabase
      .from('drivers')
      .select('driver_name, taxicaller_link_status')
      .in('taxicaller_link_status', ['unmatched', 'no_match'])
      .order('driver_name');
    if (uErr) {
      toast({ title: 'Error loading unlinked drivers', description: uErr.message, variant: 'destructive' });
    } else {
      setUnlinkedDrivers(uData || []);
    }
  };

  const pushDriverToTaxicaller = async (driverName: string) => {
    setPushingDriverName(driverName);
    try {
      const { data, error } = await supabase.functions.invoke('push-driver-to-taxicaller', {
        body: { driver_name: driverName },
      });
      if (error || data?.ok === false) {
        toast({
          title: 'Push failed',
          description: error?.message ?? data?.error ?? 'Could not push to TaxiCaller.',
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Pushed to TaxiCaller', description: `Driver ${driverName} updated.` });
      await loadTaxicallerData();
    } finally {
      setPushingDriverName(null);
    }
  };

  const dismissDriverMismatch = async (id: string) => {
    const { error } = await supabase
      .from('driver_sync_mismatches')
      .update({ status: 'dismissed', reviewed_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      toast({ title: 'Error dismissing', description: error.message, variant: 'destructive' });
      return;
    }
    setDriverMismatches((prev) => prev.filter((m) => m.id !== id));
  };

  const DRIVER_FIELD_COLUMNS: Record<string, string> = {
    dl_number: 'dl_number',
    dl_expiry: 'dl_expiry',
    id_expiry: 'id_expiry',
  };

  const applyDriverTaxicallerValue = async (m: any) => {
    const column = DRIVER_FIELD_COLUMNS[m.field_name];
    if (!column) return;
    const { error } = await supabase.from('drivers').update({ [column]: m.taxicaller_value }).eq('driver_name', m.driver_name);
    if (error) {
      toast({ title: 'Error applying value', description: error.message, variant: 'destructive' });
      return;
    }
    await supabase.from('driver_sync_mismatches').delete().eq('id', m.id);
    setDriverMismatches((prev) => prev.filter((x) => x.id !== m.id));
    toast({ title: 'Applied', description: `${column} updated from TaxiCaller value.` });
  };

  const openLinkPicker = (driverName: string) => {
    setLinkingDriverName(driverName);
    setLinkQuery(driverName);
    setLinkCandidates([]);
  };

  const searchTaxicallerDrivers = async () => {
    if (!linkQuery || linkQuery.trim().length < 2) return;
    setLinkSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('search-taxicaller-drivers', {
        body: { query: linkQuery.trim() },
      });
      if (error || data?.ok === false) {
        toast({ title: 'Search failed', description: error?.message ?? data?.error ?? 'Could not search TaxiCaller.', variant: 'destructive' });
        return;
      }
      setLinkCandidates(data?.candidates || []);
    } finally {
      setLinkSearching(false);
    }
  };

  const confirmLink = async (driverName: string, taxicallerUserId: number) => {
    const { error } = await supabase
      .from('drivers')
      .update({ taxicaller_user_id: taxicallerUserId, taxicaller_link_status: 'manually_linked' })
      .eq('driver_name', driverName);
    if (error) {
      toast({ title: 'Error linking driver', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Linked', description: `${driverName} linked to TaxiCaller.` });
    setLinkingDriverName(null);
    await loadTaxicallerData();
  };

  const markNoTaxicallerAccount = async (driverName: string) => {
    const { error } = await supabase
      .from('drivers')
      .update({ taxicaller_link_status: 'no_match' })
      .eq('driver_name', driverName);
    if (error) {
      toast({ title: 'Error updating', description: error.message, variant: 'destructive' });
      return;
    }
    await loadTaxicallerData();
  };

  useEffect(() => {
    loadAllDrivers();
    loadTaxicallerData();
  }, []);

  const visibleDrivers = useMemo(() => {
    let list = showInactive ? allDrivers : allDrivers.filter((d) => d.is_active);
    if (scopedDriverNames) {
      const scoped = new Set(scopedDriverNames);
      list = list.filter((d) => scoped.has(d.driver_name));
    }
    return list;
  }, [allDrivers, showInactive, scopedDriverNames]);

  const complianceSummary = useMemo(() => {
    let green = 0;
    let orange = 0;
    let red = 0;
    let unknown = 0;

    for (const d of visibleDrivers) {
      const u = driverUrgency(d);
      if (u === 'green') green++;
      else if (u === 'orange') orange++;
      else if (u === 'red') red++;
      else unknown++;
    }

    return { green, orange, red, unknown, total: visibleDrivers.length };
  }, [visibleDrivers]);

  const birthdayDrivers = useMemo(() => {
    const currentMonth = new Date().getMonth();
    return visibleDrivers.filter((d) => {
      if (!d.dob) return false;
      const dobDate = new Date(d.dob);
      if (Number.isNaN(dobDate.getTime())) return false;
      return dobDate.getMonth() === currentMonth;
    }).sort((a, b) => {
      const dayA = new Date(a.dob!).getDate();
      const dayB = new Date(b.dob!).getDate();
      return dayA - dayB;
    });
  }, [visibleDrivers]);

  // Filtered drivers list (search + compliance filter)
  const filteredDrivers = useMemo(() => {
    const q = driverSearch.trim().toLowerCase();
    return visibleDrivers.filter((d) => {
      if (complianceFilter !== 'all' && driverUrgency(d) !== complianceFilter) return false;
      if (!q) return true;
      return (
        (d.driver_name || '').toLowerCase().includes(q) ||
        (d.driver_firstname || '').toLowerCase().includes(q) ||
        (d.driver_lastname || '').toLowerCase().includes(q) ||
        (d.email || '').toLowerCase().includes(q) ||
        (d.phone || '').toLowerCase().includes(q) ||
        (d.ridewise_id || '').toLowerCase().includes(q)
      );
    });
  }, [visibleDrivers, driverSearch, complianceFilter]);


  const currentMonthName = new Intl.DateTimeFormat('en-NZ', { month: 'long', timeZone: getAppTimeZone() }).format(new Date());

  const getMissingItems = (driver: FullDriver): { label: string; key: string }[] => {
    const missing: { label: string; key: string }[] = [];
    if (!driver.gender) missing.push({ label: 'gender', key: 'gender' });
    if (!driver.dob) missing.push({ label: 'DOB', key: 'dob' });
    if (!driver.email) missing.push({ label: 'email', key: 'email' });
    if (!driver.phone) missing.push({ label: 'phone', key: 'phone' });
    if (!driver.ridewise_id) missing.push({ label: 'RideWise ID', key: 'ridewise_id' });
    if (!driver.dl_number) missing.push({ label: 'DL number', key: 'dl_number' });
    if (!driver.dl_expiry) missing.push({ label: 'DL expiry', key: 'dl_expiry' });
    if (!driver.id_expiry) missing.push({ label: 'ID expiry', key: 'id_expiry' });
    if (!driver.payment_type) missing.push({ label: 'payment type', key: 'payment_type' });
    if (!driver.bank_account_number) missing.push({ label: 'bank account', key: 'bank_account_number' });
    return missing;
  };

  const handleViewAsDriver = (driver: FullDriver) => {
    if (!currentUser?.isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can use the Driver View Emulator.',
        variant: 'destructive',
      });
      return;
    }

    const driverForView: Driver = {
      id: driver.driver_name,
      driver_name: driver.driver_name,
      driver_firstname: driver.driver_firstname || undefined,
      driver_lastname: driver.driver_lastname || undefined,
      email: driver.email || undefined,
      phone: driver.phone || undefined,
      password: driver.Password || '',
      rideWiseId: driver.ridewise_id || undefined,
      isAdmin: driver.AdminRights?.toLowerCase() === 'yes',
      is_active: driver.is_active,
    };

    setViewingDriver(driverForView);
    setIsViewModalOpen(true);

    toast({
      title: 'Driver View Emulator',
      description: `Opening dashboard view for ${driver.driver_name}`,
    });
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingDriver(null);
  };

  const handleEditDriver = (driver: FullDriver) => {
    setEditingDriver(driver);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingDriver(null);
  };

  const handleDriverSaved = async () => {
    await loadAllDrivers();
    await loadDriversFromDB();
  };

  const addDriver = async () => {
    if (!newDriver.driver_name?.trim()) {
      toast({ title: 'Error', description: 'Please enter a driver name', variant: 'destructive' });
      return;
    }
    if (!newDriver.Password?.trim()) {
      toast({ title: 'Error', description: 'Please enter a password', variant: 'destructive' });
      return;
    }

    const { error } = await supabase.from('drivers').insert([
      {
        driver_name: newDriver.driver_name?.trim(),
        driver_firstname: newDriver.driver_firstname?.trim() || null,
        driver_lastname: newDriver.driver_lastname?.trim() || null,
        ridewise_id: newDriver.ridewise_id?.trim() || null,
        email: newDriver.email?.trim() || null,
        phone: newDriver.phone?.trim() || null,
        Password: newDriver.Password?.trim(),
        AdminRights: newDriver.AdminRights || 'No',
        include_in_reports: newDriver.include_in_reports ?? true,
        payment_type: (newDriver.payment_type as PaymentType) || null,
        is_active: newDriver.is_active ?? true,
        gender: (newDriver.gender as DriverGender) || null,
        dob: formatDate(newDriver.dob || null) || null,
        tenant_id: DEFAULT_TENANT_ID,
      },
    ]);

    if (error) {
      console.error('Error adding driver:', error);
      toast({ title: 'Error', description: 'Failed to add driver: ' + error.message, variant: 'destructive' });
      return;
    }

    setNewDriver({
      driver_name: '',
      driver_firstname: '',
      driver_lastname: '',
      ridewise_id: '',
      email: '',
      phone: '',
      Password: '',
      AdminRights: 'No',
      include_in_reports: true,
      payment_type: '',
      is_active: true,
      gender: '',
      dob: '',
    });

    setIsAddFormOpen(false);

    await loadAllDrivers();
    await loadDriversFromDB();

    toast({ title: 'Success', description: 'Driver added successfully' });
  };

  const removeDriver = async (driverName: string) => {
    if (!confirm(`Are you sure you want to delete ${driverName}?`)) return;

    const { error } = await supabase.from('drivers').delete().eq('driver_name', driverName);

    if (error) {
      console.error('Error removing driver:', error);
      toast({ title: 'Error', description: 'Failed to remove driver', variant: 'destructive' });
      return;
    }

    await loadAllDrivers();
    await loadDriversFromDB();
    toast({ title: 'Success', description: 'Driver removed successfully' });
  };

  const openEditFromBadge = (driver: FullDriver) => {
    handleEditDriver(driver);
  };

  const addDobForAge = formatDate(newDriver.dob || null);
  const addAge = calcAge(addDobForAge);

  return (
    <FolderTabLayout
      tabs={[
        { value: 'drivers', label: 'Drivers', icon: Users },
        ...(!scopedDriverNames ? [{ value: 'add', label: 'Add New Driver', icon: UserPlus }] : []),
        { value: 'keyfobs', label: 'Key Fobs', icon: KeyRound },
        ...(!scopedDriverNames
          ? [
              {
                value: 'taxicaller',
                label: 'TaxiCaller Sync',
                icon: AlertOctagon,
                count: driverMismatches.length > 0 ? driverMismatches.length : undefined,
                tone: 'red' as const,
              },
            ]
          : []),
      ]}
      value={driverTab}
      onValueChange={(v) => setDriverTab(v as typeof driverTab)}
    >
      {/* ================= ADD NEW DRIVER TAB ================= */}
      {driverTab === 'add' && !scopedDriverNames && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New Driver
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <Label className="text-yellow-500">Driver Name (Primary Key) *</Label>
                <Input value={newDriver.driver_name || ''} onChange={(e) => setNewDriver({ ...newDriver, driver_name: e.target.value })} />
              </div>
              <div>
                <Label>First Name</Label>
                <Input value={newDriver.driver_firstname || ''} onChange={(e) => setNewDriver({ ...newDriver, driver_firstname: e.target.value })} />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input value={newDriver.driver_lastname || ''} onChange={(e) => setNewDriver({ ...newDriver, driver_lastname: e.target.value })} />
              </div>
              <div>
                <Label className="text-yellow-500">Password *</Label>
                <Input type="password" value={newDriver.Password || ''} onChange={(e) => setNewDriver({ ...newDriver, Password: e.target.value })} />
              </div>
              <div>
                <Label>RideWise ID</Label>
                <Input value={newDriver.ridewise_id || ''} onChange={(e) => setNewDriver({ ...newDriver, ridewise_id: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newDriver.email || ''} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input type="tel" value={newDriver.phone || ''} onChange={(e) => setNewDriver({ ...newDriver, phone: normalizePhoneInput(e.target.value) })} />
              </div>
              <div>
                <Label>Gender</Label>
                <Select
                  value={toSelectValue(newDriver.gender)}
                  onValueChange={(value) => setNewDriver({ ...newDriver, gender: fromSelectValue(value) as DriverGender | null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>DOB</Label>
                <Input type="date" value={formatDate(newDriver.dob || null)} onChange={(e) => setNewDriver({ ...newDriver, dob: e.target.value })} />
              </div>
              <div>
                <Label>Age</Label>
                <Input value={formatAgeLabel(addAge)} disabled className="bg-gray-700 text-gray-300" />
              </div>
              <div>
                <Label>Payment Type</Label>
                <Select
                  value={toSelectValue(newDriver.payment_type)}
                  onValueChange={(value) => setNewDriver({ ...newDriver, payment_type: fromSelectValue(value) as PaymentType | null })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox id="new-is-active" checked={newDriver.is_active ?? true} onCheckedChange={(checked) => setNewDriver({ ...newDriver, is_active: checked as boolean })} />
                <Label htmlFor="new-is-active">Active</Label>
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <Checkbox
                  id="new-include-in-reports"
                  checked={newDriver.include_in_reports ?? true}
                  onCheckedChange={(checked) => setNewDriver({ ...newDriver, include_in_reports: checked as boolean })}
                />
                <Label htmlFor="new-include-in-reports">Include in Reports</Label>
              </div>
              <div>
                <Label>Admin Rights</Label>
                <Select value={newDriver.AdminRights || 'No'} onValueChange={(value) => setNewDriver({ ...newDriver, AdminRights: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={addDriver} className="w-full mt-4">
              <Plus className="w-4 h-4 mr-2" />
              Add Driver
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ================= DRIVERS TAB ================= */}
      {driverTab === 'drivers' && (
        <div className="space-y-6">
          {/* Drivers List */}
          <Card>
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <CardTitle>
                  Drivers List ({filteredDrivers.length}
                  {filteredDrivers.length !== visibleDrivers.length ? ` of ${visibleDrivers.length}` : ''} drivers)
                </CardTitle>

                <div className="flex items-center gap-2">
                  <Checkbox id="toggle-show-inactive" checked={showInactive} onCheckedChange={(checked) => setShowInactive(checked as boolean)} />
                  <Label htmlFor="toggle-show-inactive" className="text-sm text-gray-300">
                    Show inactive
                  </Label>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Search by name, email, phone or RideWise ID…"
                    value={driverSearch}
                    onChange={(e) => setDriverSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={complianceFilter} onValueChange={(v) => setComplianceFilter(v as typeof complianceFilter)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Compliance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All compliance ({complianceSummary.total})</SelectItem>
                    <SelectItem value="green">Green ({complianceSummary.green})</SelectItem>
                    <SelectItem value="orange">Orange ({complianceSummary.orange})</SelectItem>
                    <SelectItem value="red">Red ({complianceSummary.red})</SelectItem>
                    <SelectItem value="unknown">Missing ({complianceSummary.unknown})</SelectItem>
                  </SelectContent>
                </Select>

                {(driverSearch || complianceFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => {
                      setDriverSearch('');
                      setComplianceFilter('all');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap gap-3 text-sm">
                <div className="px-3 py-2 rounded-md bg-gray-800/40 border border-gray-700">
                  <span className="text-gray-300">Compliance:</span>{' '}
                  <span className="text-green-500 font-semibold">{complianceSummary.green} Green</span>
                  <span className="text-gray-500"> • </span>
                  <span className="text-orange-400 font-semibold">{complianceSummary.orange} Orange</span>
                  <span className="text-gray-500"> • </span>
                  <span className="text-red-400 font-semibold">{complianceSummary.red} Red</span>
                  <span className="text-gray-500"> • </span>
                  <span className="text-gray-400 font-semibold">{complianceSummary.unknown} Missing</span>
                </div>
              </div>

              {birthdayDrivers.length > 0 && (
                <div className="px-3 py-2 rounded-md bg-gray-800/40 border border-purple-700/50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-purple-400 font-semibold">{currentMonthName} Birthdays:</span>
                    <span className="text-gray-400 text-xs">({birthdayDrivers.length} driver{birthdayDrivers.length !== 1 ? 's' : ''})</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {birthdayDrivers.map((driver) => {
                      const dobDate = new Date(driver.dob!);
                      const day = dobDate.getDate();
                      const ordinal = (n: number) => {
                        const s = ['th', 'st', 'nd', 'rd'];
                        const v = n % 100;
                        return n + (s[(v - 20) % 10] || s[v] || s[0]);
                      };
                      return (
                        <span
                          key={driver.driver_name}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-900/30 text-purple-300 text-xs"
                        >
                          <span className="font-medium">{driver.driver_name}</span>
                          <span className="text-purple-400/70">({ordinal(day)})</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {!showInactive && <div className="text-xs text-gray-400">Showing active drivers only. Toggle "Show inactive" to include archived drivers.</div>}
            </CardHeader>

            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-400">Loading drivers...</div>
              ) : (
                <div className="max-h-[600px] overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_rgba(255,255,255,0.06)]">

                    <TableRow>
                      <TableHead>Driver Name</TableHead>
                      <TableHead>RideWise ID</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>DL Days</TableHead>
                      <TableHead>ID Days</TableHead>
                      <TableHead>Transmission</TableHead>
                      <TableHead>Admin</TableHead>
                      <TableHead>Actions</TableHead>

                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {filteredDrivers.map((driver) => {
                      const dlU = urgencyForDays(driver.dl_days_left);
                      const idU = urgencyForDays(driver.id_days_left);
                      const dlClickable = dlU === 'red' || dlU === 'orange' || dlU === 'unknown';
                      const idClickable = idU === 'red' || idU === 'orange' || idU === 'unknown';

                      return (
                        <React.Fragment key={driver.driver_name}>
                          <TableRow className={`${!driver.is_active ? 'opacity-50' : ''} ${getMissingItems(driver).length > 0 ? 'border-b-0' : ''}`}>
                            <TableCell className="font-medium text-yellow-500">
                              {driver.driver_name}
                              {!driver.is_active && <span className="ml-2 text-xs text-red-400">(Inactive)</span>}
                            </TableCell>

                            <TableCell>
                              <span className={!driver.ridewise_id ? 'text-gray-500 italic' : ''}>{driver.ridewise_id || '-'}</span>
                            </TableCell>

                            <TableCell>
                              <span className={!driver.email ? 'text-gray-500 italic' : ''}>{driver.email || '-'}</span>
                            </TableCell>

                            <TableCell>
                              <span className={!driver.phone ? 'text-gray-500 italic' : ''}>{driver.phone || '-'}</span>
                            </TableCell>

                            <TableCell>
                              {dlClickable ? (
                                <button
                                  type="button"
                                  className={`${daysTextClass(driver.dl_days_left)} underline underline-offset-4 hover:opacity-90`}
                                  onClick={() => openEditFromBadge(driver)}
                                >
                                  {formatDaysLeft(driver.dl_days_left)}
                                </button>
                              ) : (
                                <span className={daysTextClass(driver.dl_days_left)}>{formatDaysLeft(driver.dl_days_left)}</span>
                              )}
                            </TableCell>

                            <TableCell>
                              {idClickable ? (
                                <button
                                  type="button"
                                  className={`${daysTextClass(driver.id_days_left)} underline underline-offset-4 hover:opacity-90`}
                                  onClick={() => openEditFromBadge(driver)}
                                >
                                  {formatDaysLeft(driver.id_days_left)}
                                </button>
                              ) : (
                                <span className={daysTextClass(driver.id_days_left)}>{formatDaysLeft(driver.id_days_left)}</span>
                              )}
                            </TableCell>

                            <TableCell>
                              {(() => {
                                const badge = transmissionBadge(driver.can_drive_manual);
                                return (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${badge.className}`}>
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </TableCell>

                            <TableCell>
                              <span className={driver.AdminRights?.toLowerCase() === 'yes' ? 'text-green-500 font-medium' : 'text-gray-500'}>
                                {driver.AdminRights || 'No'}
                              </span>
                            </TableCell>


                            <TableCell>
                              <div className="flex gap-1">
                                {effectivePermissions.isUnrestricted && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleViewAsDriver(driver)}
                                    className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                                {canEdit && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditDriver(driver)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="destructive" size="sm" onClick={() => removeDriver(driver.driver_name)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>

                          {getMissingItems(driver).length > 0 && (
                            <TableRow className={!driver.is_active ? 'opacity-50' : ''}>
                              <TableCell colSpan={9} className="pt-0 pb-2">

                                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                                  {getMissingItems(driver).map((item) => (
                                    <span
                                      key={item.key}
                                      className="inline-flex items-center gap-0.5 text-[10px] text-red-400/80"
                                    >
                                      <span>{item.label}</span>
                                      <X className="w-2.5 h-2.5" />
                                    </span>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {filteredDrivers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No drivers match your filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
              )}

            </CardContent>
          </Card>

          {/* Edit Modal */}
          <DriverEditModal
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
            driver={editingDriver}
            onSaved={handleDriverSaved}
          />

          {/* View Modal */}
          <DriverViewModal isOpen={isViewModalOpen} onClose={handleCloseViewModal} driver={viewingDriver} adminName={currentUser?.driver_name || 'Admin'} />
        </div>
      )}

      {/* ================= KEY FOBS TAB ================= */}
      {driverTab === 'keyfobs' && <KeyFobsTab isAdmin={canEdit} scopedDriverNames={scopedDriverNames} />}

      {/* ================= TAXICALLER SYNC TAB ================= */}
      {driverTab === 'taxicaller' && !scopedDriverNames && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertOctagon className="w-5 h-5 text-yellow-500" />
                TaxiCaller Sync Review
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                DMS pushes each driver's licence number, DL expiry and ID expiry to TaxiCaller whenever you save a
                driver. This list shows any field the nightly check still finds different between the two systems.
                Push DMS's value across, accept TaxiCaller's value into DMS, or dismiss if the difference is expected.
              </p>
            </CardHeader>
            <CardContent>
              {driverMismatches.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No mismatches -- DMS and TaxiCaller agree on every linked driver.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>DMS Value</TableHead>
                      <TableHead>TaxiCaller Value</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {driverMismatches.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell className="font-medium">{m.driver_name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.field_name}</TableCell>
                        <TableCell>{m.dms_value ?? <span className="text-muted-foreground">(blank)</span>}</TableCell>
                        <TableCell>{m.taxicaller_value ?? <span className="text-muted-foreground">(blank)</span>}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={pushingDriverName === m.driver_name}
                            onClick={() => pushDriverToTaxicaller(m.driver_name)}
                          >
                            {pushingDriverName === m.driver_name ? 'Pushing...' : 'Push DMS Value'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => applyDriverTaxicallerValue(m)}>
                            Apply TaxiCaller Value
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => dismissDriverMismatch(m.id)}>
                            Dismiss
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-yellow-500" />
                Unlinked Drivers
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                TaxiCaller has no reliable driver ID to match against by name alone, so these drivers couldn't be
                auto-linked (name not found, or more than one possible match in TaxiCaller). Search and link the
                correct TaxiCaller account, or confirm the driver genuinely has none.
              </p>
            </CardHeader>
            <CardContent>
              {unlinkedDrivers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Every driver is linked to a TaxiCaller account (or confirmed to have none).</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unlinkedDrivers.map((d) => (
                      <React.Fragment key={d.driver_name}>
                        <TableRow>
                          <TableCell className="font-medium">{d.driver_name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {d.taxicaller_link_status === 'no_match' ? 'Confirmed no TaxiCaller account' : 'Not yet reviewed'}
                          </TableCell>
                          <TableCell className="text-right space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openLinkPicker(d.driver_name)}>
                              Link to TaxiCaller
                            </Button>
                            {d.taxicaller_link_status !== 'no_match' && (
                              <Button size="sm" variant="ghost" onClick={() => markNoTaxicallerAccount(d.driver_name)}>
                                No TaxiCaller Account
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                        {linkingDriverName === d.driver_name && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <div className="flex flex-col gap-2 py-2">
                                <div className="flex gap-2">
                                  <Input
                                    value={linkQuery}
                                    onChange={(e) => setLinkQuery(e.target.value)}
                                    placeholder="Search TaxiCaller by name"
                                    className="max-w-xs"
                                  />
                                  <Button size="sm" disabled={linkSearching} onClick={searchTaxicallerDrivers}>
                                    {linkSearching ? 'Searching...' : 'Search'}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setLinkingDriverName(null)}>
                                    Cancel
                                  </Button>
                                </div>
                                {linkCandidates.length > 0 && (
                                  <div className="flex flex-col gap-1">
                                    {linkCandidates.map((c) => (
                                      <div key={c.id} className="flex items-center justify-between border border-border rounded px-3 py-1.5">
                                        <span>{c.first_name} {c.last_name}</span>
                                        <Button size="sm" onClick={() => confirmLink(d.driver_name, c.id)}>
                                          Confirm Link
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </FolderTabLayout>
  );
};

export default DriverManagement;
